import { forwardRef } from 'react';
import { SelectProps } from '@theme/components/types';
import clsx from 'clsx';
import { Icon } from './icon';
import { twMerge } from 'tailwind-merge';

const Select = forwardRef<HTMLSelectElement, SelectProps>((props, ref) => {
  const {
    className,
    options,
    borderless = false,
    icon,
    iconSize,
    error,
    label,
    labelClassName,
    required = false,
    ...rest
  } = props;

  return (
    <label
      className={clsx(
        'flex flex-col relative text-xs text-gray-800',
        labelClassName,
        {
          'pl-7': icon
        }
      )}
    >
      {icon && (
        <Icon
          name={icon}
          size={iconSize ?? 20}
          className="absolute left-0 top-1/2 transform -translate-y-1/2"
        />
      )}

      {label && (
        <span className={clsx(['mb-2', error && 'text-error'])}>
          {label} {required && <span>*</span>}
        </span>
      )}
      <div className="relative">
        <select
          {...rest}
          ref={ref}
          className={twMerge(
            clsx(
              'cursor-pointer truncate h-10 w-40 px-2.5 shrink-0 outline-none rounded-[0.25rem] text-base md:text-sm appearance-none',
              !borderless &&
                'border border-[#A5A5A5]/30 transition-all duration-150 hover:border-black focus:border-black',
              error && 'border-error focus:border-error'
            ),
            className
          )}
        >
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              className={option.class}
            >
              {option.label}
            </option>
          ))}
        </select>
        <Icon
          name="chevron-down"
          size={16}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none"
        />
      </div>

      {/* Display error message if present */}
      {error && (
        <span className="mt-1 text-xs text-error">{error.message}</span>
      )}
    </label>
  );
});

Select.displayName = 'Select';

export { Select };
